import React, { useId } from "react";
import { ChevronDown } from "lucide-react";

interface FaqItemProps {
  question: string;
  /** Paragraphs. A `<b>` marker wraps any phrase that should be bold. */
  answer: string[];
  isOpen: boolean;
  onToggle: () => void;
}

const renderAnswer = (paragraph: string): React.ReactNode =>
  paragraph.split(/<b>|<\/b>/).map((chunk, index) =>
    index % 2 === 1 ? (
      <strong className="font-bold" key={index}>
        {chunk}
      </strong>
    ) : (
      <React.Fragment key={index}>{chunk}</React.Fragment>
    ),
  );

const FaqItem: React.FC<FaqItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle,
}) => {
  const id = useId();
  const triggerId = `faq-trigger-${id}`;
  const panelId = `faq-panel-${id}`;

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white transition-all duration-300 dark:bg-slate-800 ${
        isOpen
          ? "border-primary/25 shadow-md"
          : "border-gray-200 dark:border-slate-700"
      }`}
    >
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-6 px-7 py-5 text-left transition-colors hover:bg-cream dark:hover:bg-slate-700"
        id={triggerId}
        onClick={onToggle}
        type="button"
      >
        <span className="pr-4 text-base font-bold leading-snug tracking-tight text-gray-900 dark:text-white">
          {question}
        </span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-primary transition-transform duration-300 dark:text-primary-light ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        aria-labelledby={triggerId}
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-[60rem]" : "max-h-0"
        }`}
        id={panelId}
        role="region"
      >
        <div className="space-y-3 px-7 pb-6 pt-1">
          {answer.map((paragraph, index) => (
            <p
              className="text-base leading-relaxed text-gray-500 dark:text-gray-400"
              key={index}
            >
              {renderAnswer(paragraph)}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FaqItem;
